import { looksTemporalForChart } from "./chart-field-guess";
import { isPrimaryKey, isStructured, type Field } from "./schema-field";

/**
 * Free-text / identity names that must not become a "count by" category axis.
 * Kept in sync with suggest-charts: Customer Name, Order ID, emails, …
 */
const NEVER_CATEGORY =
  /^(id|uuid|guid|pk|name|title|label|description|desc|note|notes|comment|comments|bio|body|content|text|message|email|e[_-]?mail|phone|mobile|url|uri|link|href|path|slug|token|hash|password|passwd|secret|key|address|street|city|zip|postal|code|customer|client|user|owner)$|_id$|_(name|title|label|email|phone|address)$|^(customer|client|user|owner)_/i;

/**
 * Measure-named columns that make a useless category axis (Quantity, Unit Price).
 * Same compound carve-out as chart-field-guess so `price_tier` stays strong.
 */
const MEASURE_NAME =
  /(amount|total|sum|budget|cost|price|value|revenue|spend|score|qty|quantity|count|hours|duration)(?![\w-]*(?:tier|level|grade|type|kind|category|class|status)\b)/i;

/** Calendar part columns the generator may intentionally group by. */
const DISCRETE_TEMPORAL = /^(year|month|day|week)$/i;

const normalize = (name: string): string =>
  name.trim().toLowerCase().replace(/[\s-]+/g, "_");

const isNeverCategory = (field: Field): boolean =>
  NEVER_CATEGORY.test(normalize(field.name));

const isMeasureNamed = (field: Field): boolean =>
  MEASURE_NAME.test(field.name) || MEASURE_NAME.test(normalize(field.name));

export type ChartAxisRef = {
  groupBy?: string | null;
  bucket?: string | null;
  measure?: string | null;
};

/**
 * True when a column must not be the category axis of a count-by chart.
 *
 * Catches Quantity / Unit Price / Total (measure-named), primary keys,
 * free-text identity columns, and structured blobs. A month bucket is
 * allowed only on a temporal column.
 *
 * Does NOT treat every NUMBER column as weak: a Postgres `rating` int or
 * `year` int can be a useful discrete axis from /ui/generate. Enums
 * (including `price_tier`) stay strong even when the name embeds a measure
 * word.
 */
export function isWeakCategoryAxis(
  field: Field | undefined,
  chart?: ChartAxisRef,
): boolean {
  // Unknown column: leave the stored chart alone. Test fixtures and older
  // analyse payloads often omit fields the chart still names.
  if (!field) return false;

  if (chart?.bucket === "month") {
    return !looksTemporalForChart(field);
  }

  // Enums are categorical by definition — keep generated count-by-enum charts.
  if (field.isEnum) return false;

  // Measure-named axes only. looksNumericForChart also returns true for any
  // typed NUMBER (rating, year), which would false-positive good Postgres
  // charts; name hints catch the Sample Orders Quantity / Unit Price case.
  if (isMeasureNamed(field)) return true;
  if (isPrimaryKey(field) || isNeverCategory(field)) return true;
  if (isStructured(field)) return true;
  // A bare date used as a categorical (no month bucket) is not a useful axis.
  // Discrete calendar parts (year, month, …) are intentional groupBy values.
  if (
    looksTemporalForChart(field) &&
    !DISCRETE_TEMPORAL.test(normalize(field.name))
  ) {
    return true;
  }
  return false;
}

/**
 * True when stored / generated charts should yield to the schema heuristic.
 *
 * After isUnplottableSeries stopped hiding equal-count bars, Sample Orders
 * kept the generator's "count by Quantity" and "count by Unit Price" charts
 * and never reached suggestChartsFromSchema. Any weak category axis in the
 * stored set is enough to prefer the fallback.
 */
export function storedChartsNeedFallback(
  charts: ChartAxisRef[] | undefined,
  fields: Field[] | undefined,
): boolean {
  if (!charts?.length) return true;
  if (!fields?.length) return false;

  return charts.some((chart) => {
    if (!chart.groupBy) return true;
    const field = fields.find((candidate) => candidate.name === chart.groupBy);
    return isWeakCategoryAxis(field, chart);
  });
}
